import { motion } from "framer-motion";
import { Route, Zap, Layers, ArrowUpRight, Snowflake, Thermometer, Gauge, ShieldCheck } from "lucide-react";
import { ASSETS, scrollToId } from "@/lib/site";

const SPECIALIZED_IMG = ASSETS.heroTruck;
const OTR_IMG = ASSETS.fleet;
const FLATBED_IMG = ASSETS.flatbed;

const rise = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 },
  }),
};

function SpecItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand" strokeWidth={1.75} />
      <div>
        <div className="text-xs uppercase tracking-widest text-[#71717a]">{label}</div>
        <div className="text-sm font-medium text-[#e4e4e7]">{value}</div>
      </div>
    </div>
  );
}

function TextCard({ i, icon: Icon, tag, title, desc, className }) {  return (
    <motion.div
      variants={rise}
      custom={i}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.3 }}
      data-testid={`service-${tag.toLowerCase().replace(/[^a-z]/g, "-")}`}
      className={`group flex flex-col justify-between border border-white/10 bg-surface p-8 transition-colors duration-300 hover:bg-surfaceHover md:p-10 ${className}`}
    >
      <div className="flex items-start justify-between">
        <Icon className="h-8 w-8 text-brand" strokeWidth={1.5} />
        <span className="text-xs uppercase tracking-widest text-[#71717a]">0{i + 1}</span>
      </div>
      <div className="mt-14">
        <h3 className="font-display text-2xl font-bold uppercase tracking-tight md:text-3xl">{title}</h3>
        <p className="mt-3 max-w-sm text-sm leading-relaxed text-[#a1a1a8]">{desc}</p>
      </div>
    </motion.div>
  );
}

function ImageCard({ i, num, img, title, className }) {
  return (
    <motion.div
      variants={rise}
      custom={i}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.3 }}
      className={`group relative overflow-hidden border border-white/10 ${className}`}
    >
      <img
        src={img}
        alt={title}
        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" />
      <span className="absolute right-6 top-6 text-xs uppercase tracking-widest text-white/70">{num}</span>
      <div className="absolute bottom-0 left-0 flex w-full items-end justify-between p-6">
        <h3 className="font-display text-xl font-bold uppercase tracking-tight md:text-2xl">{title}</h3>
        <ArrowUpRight className="h-6 w-6 text-brand" />
      </div>
    </motion.div>
  );
}

export default function Services() {
  return (
    <section id="services" data-testid="services" className="mx-auto max-w-[1600px] px-5 py-24 md:px-10 md:py-36">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end"
      >
        <div>
          <span className="text-xs uppercase tracking-[0.3em] text-brand">What We Haul</span>
          <h2 className="mt-4 font-display text-4xl font-black uppercase leading-none tracking-tighter md:text-6xl">
            Full-Service
            <br />
            Freight Capacity
          </h2>
        </div>
        <p className="max-w-sm text-sm leading-relaxed text-[#a1a1a8]">
          From over-dimensional specialized loads to reliable long-haul lanes, we match the right
          equipment to every shipment.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.25 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        data-testid="service-reefer"
        className="group relative mb-3 grid grid-cols-1 overflow-hidden border border-white/10 md:grid-cols-2"
      >
        <div className="relative min-h-[300px] overflow-hidden md:min-h-[420px]">
          <img
            src={ASSETS.reefer}
            alt="Refrigerated reefer trailer on a Texas highway"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent md:bg-gradient-to-r" />
        </div>
        <div className="flex flex-col justify-between gap-8 bg-surface p-8 md:p-12">
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-2 border border-brand/40 bg-brand/10 px-3 py-1.5 text-xs uppercase tracking-widest text-brand">
              <Snowflake className="h-4 w-4" /> Our Specialty
            </span>
            <span className="text-xs uppercase tracking-widest text-[#71717a]">00</span>
          </div>
          <div>
            <h3 className="font-display text-3xl font-black uppercase leading-none tracking-tighter md:text-5xl">
              Reefer /
              <br />
              Refrigerated
            </h3>
            <p className="mt-4 max-w-md text-base leading-relaxed text-[#a1a1a8]">
              Temperature-controlled freight is what we run most. Produce, frozen goods, pharma and
              other cold-chain loads hauled at the right temp, monitored the whole way, and delivered
              on schedule.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-4">
              <SpecItem icon={Thermometer} label="Temp Range" value="-20°F to 70°F" />
              <SpecItem icon={Snowflake} label="Loads" value="Frozen & fresh" />
              <SpecItem icon={Gauge} label="Monitoring" value="Live temp tracking" />
              <SpecItem icon={ShieldCheck} label="Trailer" value="Food-grade, washed out" />
            </div>
          </div>
          <button
            onClick={() => scrollToId("#contact")}
            data-testid="reefer-quote-btn"
            className="group/btn flex w-fit items-center gap-2 bg-brand px-7 py-4 text-sm font-semibold uppercase tracking-wide text-ink transition-colors hover:bg-white"
          >
            Get a Reefer Quote
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
          </button>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3 md:grid-rows-2">
        <ImageCard i={0} num="01" img={SPECIALIZED_IMG} title="Specialized Freight" className="md:col-span-2 md:row-span-2 min-h-[320px] md:min-h-0" />
        <TextCard
          i={1}
          icon={Route}
          tag="OTR"
          title="Long-Distance / OTR"
          desc="Coast-to-coast lanes across the lower 48 with consistent, tracked transit times."
        />
        <TextCard
          i={2}
          icon={Zap}
          tag="Expedited"
          title="Expedited"
          desc="Time-critical loads moved fast when the delivery window can't slip."
        />
        <ImageCard i={3} num="04" img={OTR_IMG} title="Dry Van" className="min-h-[240px]" />
        <TextCard
          i={4}
          icon={Layers}
          tag="Flatbed"
          title="Flatbed"
          desc="Secured, tarped, and permitted flatbed hauls for building materials, machinery and more."
          className="md:col-span-1"
        />
        <ImageCard i={5} num="06" img={FLATBED_IMG} title="Heavy / Over-Dimensional" className="md:col-span-1 min-h-[240px]" />
      </div>
    </section>
  );
}
